import { CheckCircle } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useState } from "react";

import { useSetWorkStateMutation } from "../../store/services/crmApi";
import { Work, WorkItemStateEnum, WorkStateEnum } from "../../store/types/work.types";

interface CloseWorkButtonProps {
  work: Work;
  onClose?: () => void;
}

const CloseWorkButton = ({ work, onClose }: CloseWorkButtonProps) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [setWorkState, { isLoading }] = useSetWorkStateMutation();

  // Check if work is already closed
  if (work.State === WorkStateEnum.Closed || work.State === WorkStateEnum.Solved) {
    return null;
  }

  // Check if there are any open workitems
  const openWorkItems = work.WorkItems?.filter(
    item => item.State === WorkItemStateEnum.Active
  ) || [];
  
  const hasOpenWorkItems = openWorkItems.length > 0;

  const handleClick = () => {
    if (hasOpenWorkItems) {
      // Show warning dialog
      setConfirmDialogOpen(true);
    } else {
      // Close work directly
      handleCloseWork();
    }
  };

  const handleCloseWork = async () => {
    try {
      await setWorkState({
        workId: work.Id,
        workState: WorkStateEnum.Closed
      }).unwrap();
      
      setConfirmDialogOpen(false);
      onClose?.();
    } catch (error) {
      // Error is handled by the mutation with toast
      console.error("Failed to close work:", error);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="contained"
        color="success"
        size="large"
        startIcon={<CheckCircle />}
        disabled={isLoading}
        sx={{ borderRadius: 2 }}
      >
        Avsluta ärende
      </Button>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {hasOpenWorkItems ? "Varning - Öppna aktiviteter" : "Bekräfta avslutning"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasOpenWorkItems ? (
              <>
                Det finns fortfarande {openWorkItems.length} öppna aktiviteter kopplade till detta ärende:
                <ul style={{ marginTop: 8 }}>
                  {openWorkItems.slice(0, 5).map(item => (
                    <li key={item.Id}>
                      {item.Name || `Aktivitet #${item.Id}`}
                    </li>
                  ))}
                  {openWorkItems.length > 5 && (
                    <li>...och {openWorkItems.length - 5} till</li>
                  )}
                </ul>
                <strong>Rekommendation:</strong> Stäng alla aktiviteter innan du avslutar ärendet.
                <br /><br />
                Vill du ändå avsluta ärendet?
              </>
            ) : (
              "Är du säker på att du vill avsluta detta ärende?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialogOpen(false)}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button 
            onClick={handleCloseWork}
            color={hasOpenWorkItems ? "warning" : "success"}
            variant="contained"
            disabled={isLoading}
          >
            {hasOpenWorkItems ? "Avsluta ändå" : "Ja, avsluta"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CloseWorkButton;